import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AuthContext } from './AuthContext';
import { apiGet } from '../utils/api';
import {
  WALKTHROUGH_BUILD_ENABLED,
  WALKTHROUGH_SYNC_BUILD_ENABLED,
} from '../config/features';

const FeatureFlagsContext = createContext(null);

const INITIAL_FLAGS = Object.freeze({
  walkthroughEnabled: false,
  walkthroughSyncEnabled: false,
  loaded: false,
});

export function FeatureFlagsProvider({ children }) {
  const { user, isLoading: authLoading } = useContext(AuthContext);
  const [flags, setFlags] = useState(INITIAL_FLAGS);

  const refreshFeatureFlags = useCallback(async () => {
    if (!user) {
      setFlags(INITIAL_FLAGS);
      return;
    }

    try {
      const result = await apiGet('/api/configurations/feature-flags');
      if (result.success && result.data) {
        setFlags({
          walkthroughEnabled: Boolean(result.data.walkthroughEnabled),
          walkthroughSyncEnabled: Boolean(result.data.walkthroughSyncEnabled),
          loaded: true,
        });
        return;
      }
    } catch {
      /* fall through to safe defaults */
    }

    setFlags({ ...INITIAL_FLAGS, loaded: true });
  }, [user]);

  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    void refreshFeatureFlags();
    return undefined;
  }, [authLoading, refreshFeatureFlags]);

  const value = useMemo(
    () => ({
      ...flags,
      refreshFeatureFlags,
      isWalkthroughEnabled: WALKTHROUGH_BUILD_ENABLED && flags.walkthroughEnabled,
      isWalkthroughSyncEnabled: WALKTHROUGH_SYNC_BUILD_ENABLED && flags.walkthroughSyncEnabled,
    }),
    [flags, refreshFeatureFlags]
  );

  return (
    <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
  );
}

/**
 * @returns {{
 *   walkthroughEnabled: boolean,
 *   walkthroughSyncEnabled: boolean,
 *   loaded: boolean,
 *   isWalkthroughEnabled: boolean,
 *   isWalkthroughSyncEnabled: boolean,
 *   refreshFeatureFlags: () => Promise<void>,
 * }}
 */
export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  }
  return context;
}
